import { z } from 'zod';
import { parseResponseReference } from './tool-response-projection.js';
/**
 * What a successful call to a configured tool means happened in the partner's system.
 *
 * An engagement is a partner-owned unit of customer work — an appointment, visit, work order,
 * rental, or job. Partners display their own noun; Reach stores the normalized concept.
 */
export var ToolEngagementActionTypeEnum;
(function (ToolEngagementActionTypeEnum) {
    /** The call created an engagement, and the mapped result carries its ID. */
    ToolEngagementActionTypeEnum["ENGAGEMENT_CREATED"] = "engagement_created";
    /**
     * The call created and sent the customer a link they can book through. A successful call is
     * itself the evidence — no output is required, and the raw link is never persisted. It does not
     * mean an engagement exists; one only does once the partner syncs it.
     */
    ToolEngagementActionTypeEnum["ENGAGEMENT_LINK_CREATED"] = "engagement_link_created";
})(ToolEngagementActionTypeEnum || (ToolEngagementActionTypeEnum = {}));
export const toolEngagementActionTypeEnum = z.nativeEnum(ToolEngagementActionTypeEnum);
/** Reserved engagement-classification keys stored in a tool's `schema_mapping_refs`. */
export const ENGAGEMENT_ACTION_REF_KEY = 'engagementAction';
export const ENGAGEMENT_SCHEMA_REF_KEY = 'engagementSchema';
/**
 * The reserved `response_mapping` output that carries the partner's engagement ID. Partners map
 * their own path to it — a booking API's `data.appointment_id` becomes `engagementId` — so the
 * Voice adapter reads one well-known key instead of parsing partner-specific shapes.
 */
export const ENGAGEMENT_ID_OUTPUT_KEY = 'engagementId';
/**
 * The action type a value names, or `null` for an absent or unrecognized one. Narrowing rather than
 * throwing keeps a stray value from breaking tool execution; validation is what reports it to the
 * partner. Callers reading a raw form value get the same treatment as callers reading stored refs.
 */
export function parseEngagementActionType(value) {
    const parsed = toolEngagementActionTypeEnum.safeParse(value);
    return parsed.success ? parsed.data : null;
}
/** The action type a tool's `schema_mapping_refs` declares, if it declares a recognized one. */
export function readEngagementActionType(schemaMappingRefs) {
    return parseEngagementActionType(schemaMappingRefs?.[ENGAGEMENT_ACTION_REF_KEY]);
}
/**
 * Whether a tool claims to be an engagement action at all, regardless of whether the claim is
 * well-formed. Validation uses this to tell "not an engagement tool" apart from "an engagement tool
 * configured wrongly", so a half-finished classification is reported instead of silently ignored.
 */
export function claimsEngagementAction(schemaMappingRefs) {
    if (!schemaMappingRefs)
        return false;
    return (Object.prototype.hasOwnProperty.call(schemaMappingRefs, ENGAGEMENT_ACTION_REF_KEY) ||
        Object.prototype.hasOwnProperty.call(schemaMappingRefs, ENGAGEMENT_SCHEMA_REF_KEY));
}
/**
 * The `schema_mapping_refs` entries for a classification, merged over whatever the tool already
 * carries so unrelated references survive.
 *
 * An absent schema clears the key rather than writing an empty string — a half-finished
 * classification should read as no reference at all, not as a reference to nothing. Validation is
 * what stops it from being saved that way. Both action and schema updates use this helper so they
 * preserve the same merge and clearing behavior.
 */
export function withToolEngagementAction(args) {
    const schemaDefinitionId = args.engagementSchemaDefinitionId?.trim();
    const unrelatedRefs = Object.fromEntries(Object.entries(args.schemaMappingRefs ?? {}).filter(([key]) => key !== ENGAGEMENT_SCHEMA_REF_KEY));
    return {
        ...unrelatedRefs,
        [ENGAGEMENT_ACTION_REF_KEY]: args.actionType,
        ...(schemaDefinitionId ? { [ENGAGEMENT_SCHEMA_REF_KEY]: schemaDefinitionId } : {}),
    };
}
/** The tool's `schema_mapping_refs` with the classification removed, leaving other refs intact. */
export function withoutToolEngagementAction(schemaMappingRefs) {
    return Object.fromEntries(Object.entries(schemaMappingRefs ?? {}).filter(([key]) => key !== ENGAGEMENT_ACTION_REF_KEY && key !== ENGAGEMENT_SCHEMA_REF_KEY));
}
/** Whether the reserved engagement-ID output is one valid leaf without wildcard array traversal. */
export function mapsEngagementId(responseMapping) {
    if (!responseMapping)
        return false;
    const mapping = responseMapping[ENGAGEMENT_ID_OUTPUT_KEY];
    if (typeof mapping !== 'string')
        return false;
    const parsedMapping = parseResponseReference(mapping);
    return parsedMapping.success && parsedMapping.tokens.every(token => token.type !== 'each');
}
export function requiresEngagementIdMapping(schemaMappingRefs, responseMapping) {
    return (readEngagementActionType(schemaMappingRefs) === ToolEngagementActionTypeEnum.ENGAGEMENT_CREATED &&
        !mapsEngagementId(responseMapping));
}
//# sourceMappingURL=tool-engagement.js.map